import type { Currency, Period } from "../../api/reporting";

/**
 * The period model behind the sales screens.
 *
 * Lives apart from the analysis page because the client drill-down and the
 * margin page both need it, and a page importing another page to borrow a type
 * is how two screens quietly become one.
 */

/** A span of dates the aggregates are fetched for. */
export interface Window_ {
  key: string;
  label: string;
  period: Period;
  /** User-added columns can be removed; the three base ones cannot. */
  custom?: boolean;
}

/**
 * Which window the current one is measured against.
 *
 * `previous` is September against August, `same-last-year` is September against
 * September a year ago. The year column ignores this: its previous period and
 * its same-period-last-year are the same window.
 *
 * Named `Comparison` and not `Basis`: basis now means billed against net, and
 * two types called Basis in one file is the ambiguity this whole change exists
 * to remove.
 */
export type Comparison = "previous" | "same-last-year";

/** One column of the grid: a window, and the window it is judged against. */
export interface PeriodColumn {
  /** The selection's key — a preset name, or a custom `y2024` / `r…` key. */
  key: string;
  current: Window_;
  compare: Window_;
}

export function iso(date: Date): string {
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
}

const lastDayOf = (year: number, month: number) => new Date(year, month + 1, 0).getDate();
const clampDay = (year: number, month: number, day: number) =>
  Math.min(day, lastDayOf(year, month));

/** Normalises an out-of-range month, so month -1 becomes December of last year. */
function normalise(year: number, month: number): { year: number; month: number } {
  const date = new Date(year, month, 1);
  return { year: date.getFullYear(), month: date.getMonth() };
}

const monthLabel = (year: number, month: number) =>
  new Date(year, month, 1).toLocaleString(undefined, { month: "long", year: "numeric" });

const monthShort = (year: number, month: number) =>
  new Date(year, month, 1).toLocaleString(undefined, { month: "short" });

const quarterLabel = (year: number, startMonth: number) =>
  `Q${Math.floor(startMonth / 3) + 1} ${year}`;

/* ---------------------------------------------------------------- model --- */

/**
 * Where a window sits on the calendar.
 *
 * Enough to derive both the window itself and what it is compared against,
 * for any preset or custom span. A month knows its previous month and the same
 * month a year ago; a span of days knows the same span before it and the same
 * dates a year earlier. Everything the strip can offer is one of these four.
 */
export type Anchor =
  | { kind: "month"; year: number; month: number }
  | { kind: "quarter"; year: number; month: number }
  | { kind: "year"; year: number }
  | { kind: "days"; from: string; to: string };

/** One chip on the strip: a name, an anchor, and whether the user made it. */
export interface Selection {
  key: string;
  anchor: Anchor;
  /** A custom span or year, removable from the strip. */
  custom?: boolean;
}

export type PresetKey =
  | "this-month"
  | "this-quarter"
  | "ytd"
  | "last-month"
  | "last-quarter"
  | "last-year"
  | "last-12-months"
  | "last-30-days"
  | "last-7-days";

export const PRESETS: PresetKey[] = [
  "this-month",
  "this-quarter",
  "ytd",
  "last-month",
  "last-quarter",
  "last-year",
  "last-12-months",
  "last-30-days",
  "last-7-days",
];

/** The three fixed columns of the dashboard summary and the drill-down. */
export const DEFAULT_PRESETS: PresetKey[] = ["this-month", "this-quarter", "ytd"];

/** What the analysis page opens on. */
export const ANALYSIS_PRESETS: PresetKey[] = ["this-month", "last-month", "this-quarter", "ytd"];

const shiftDays = (date: string, days: number): string => {
  const [y, m, d] = date.split("-").map(Number) as [number, number, number];
  return iso(new Date(y, m - 1, d + days));
};
const shiftYears = (date: string, years: number): string => {
  const [y, m, d] = date.split("-").map(Number) as [number, number, number];
  return iso(new Date(y + years, m - 1, clampDay(y + years, m - 1, d)));
};
const spanDays = (from: string, to: string): number => {
  const [fy, fm, fd] = from.split("-").map(Number) as [number, number, number];
  const [ty, tm, td] = to.split("-").map(Number) as [number, number, number];
  return Math.round((new Date(ty, tm - 1, td).getTime() - new Date(fy, fm - 1, fd).getTime()) / 86_400_000) + 1;
};

export function presetSelection(key: PresetKey, now = new Date()): Selection {
  const year = now.getFullYear();
  const month = now.getMonth();
  const today = iso(now);
  const quarterStart = Math.floor(month / 3) * 3;

  switch (key) {
    case "this-month":
      return { key, anchor: { kind: "month", year, month } };
    case "this-quarter":
      return { key, anchor: { kind: "quarter", year, month: quarterStart } };
    case "ytd":
      return { key, anchor: { kind: "year", year } };
    case "last-month":
      return { key, anchor: { kind: "month", ...normalise(year, month - 1) } };
    case "last-quarter":
      return { key, anchor: { kind: "quarter", ...normalise(year, quarterStart - 3) } };
    case "last-year":
      return { key, anchor: { kind: "year", year: year - 1 } };
    case "last-12-months":
      return { key, anchor: { kind: "days", from: shiftDays(shiftYears(today, -1), 1), to: today } };
    case "last-30-days":
      return { key, anchor: { kind: "days", from: shiftDays(today, -29), to: today } };
    case "last-7-days":
      return { key, anchor: { kind: "days", from: shiftDays(today, -6), to: today } };
  }
}

export function customYear(year: number): Selection {
  return { key: `y${year}`, anchor: { kind: "year", year }, custom: true };
}

export function customSpan(from: string, to: string): Selection {
  return { key: `r${from}_${to}`, anchor: { kind: "days", from, to }, custom: true };
}

/**
 * One column: the selection's window and the window it is compared against.
 *
 * The comparison rules are the ones the three original columns used, applied
 * to any anchor. *Previous* is the equivalent stretch immediately before —
 * the month before, the quarter before, the same number of days before. *Same
 * last year* is the same window a year earlier.
 *
 * A window that contains today is always cut at today, and the window it is
 * compared against is cut at the same day-of-period, so an unfinished
 * September is never read against a whole August. There used to be a toggle
 * for this; it did nothing for six of the nine presets, and the alternative it
 * offered for the other three was the collapse-shaped comparison. A completed
 * period is whole on both sides.
 */
export function resolveColumn(selection: Selection, comparison: Comparison, now = new Date()): PeriodColumn {
  const { anchor } = selection;
  const year = now.getFullYear();
  const month = now.getMonth();
  const day = now.getDate();

  const keys = { current: `${selection.key}_current`, compare: `${selection.key}_compare` };

  if (anchor.kind === "month") {
    const isCurrent = anchor.year === year && anchor.month === month;
    const cutDay = isCurrent ? day : null;
    const at = (y: number, m: number, key: string): Window_ => ({
      key,
      label: cutDay === null ? monthLabel(y, m) : `${monthShort(y, m)} 1–${clampDay(y, m, cutDay)}, ${y}`,
      period: {
        from: iso(new Date(y, m, 1)),
        to: iso(new Date(y, m, cutDay === null ? lastDayOf(y, m) : clampDay(y, m, cutDay))),
      },
    });
    const before =
      comparison === "previous"
        ? normalise(anchor.year, anchor.month - 1)
        : normalise(anchor.year - 1, anchor.month);
    return {
      key: selection.key,
      current: at(anchor.year, anchor.month, keys.current),
      compare: at(before.year, before.month, keys.compare),
    };
  }

  if (anchor.kind === "quarter") {
    const isCurrent = anchor.year === year && anchor.month === Math.floor(month / 3) * 3;
    // How far into the quarter today is, so the compared quarter is cut at the
    // same place rather than at a raw day count.
    const into = isCurrent ? { months: month - anchor.month, day } : null;
    const at = (y: number, m: number, key: string): Window_ => {
      const end = into === null ? normalise(y, m + 2) : normalise(y, m + into.months);
      const endDay = into === null ? lastDayOf(end.year, end.month) : clampDay(end.year, end.month, into.day);
      return {
        key,
        label: into === null ? quarterLabel(y, m) : `${quarterLabel(y, m)} to ${monthShort(end.year, end.month)} ${endDay}`,
        period: {
          from: iso(new Date(y, m, 1)),
          to: iso(new Date(end.year, end.month, endDay)),
        },
      };
    };
    const before =
      comparison === "previous"
        ? normalise(anchor.year, anchor.month - 3)
        : normalise(anchor.year - 1, anchor.month);
    return {
      key: selection.key,
      current: at(anchor.year, anchor.month, keys.current),
      compare: at(before.year, before.month, keys.compare),
    };
  }

  if (anchor.kind === "year") {
    // The current year is always year-to-date: there is no complete current
    // year to show. The toggle decides whether the compared year is read
    // complete or cut to the same date. A past year is complete on both sides.
    const isCurrent = anchor.year === year;
    const at = (y: number, key: string, toDate: boolean): Window_ => ({
      key,
      label: toDate ? `${y} YTD` : String(y),
      period: {
        from: iso(new Date(y, 0, 1)),
        to: toDate ? iso(new Date(y, month, clampDay(y, month, day))) : iso(new Date(y, 11, 31)),
      },
    });
    return {
      key: selection.key,
      current: at(anchor.year, keys.current, isCurrent),
      compare: at(anchor.year - 1, keys.compare, isCurrent),
    };
  }

  // A span of days: the same number of days immediately before, or the same
  // dates a year earlier. No cut — the span is explicit.
  const length = spanDays(anchor.from, anchor.to);
  const compareFrom = comparison === "previous" ? shiftDays(anchor.from, -length) : shiftYears(anchor.from, -1);
  const compareTo = comparison === "previous" ? shiftDays(anchor.from, -1) : shiftYears(anchor.to, -1);
  const label = (from: string, to: string) => `${shortDate(from)} → ${shortDate(to)}`;
  return {
    key: selection.key,
    current: { key: keys.current, label: label(anchor.from, anchor.to), period: { from: anchor.from, to: anchor.to } },
    compare: { key: keys.compare, label: label(compareFrom, compareTo), period: { from: compareFrom, to: compareTo } },
  };
}

const shortDate = (date: string): string => {
  const [y, m, d] = date.split("-").map(Number) as [number, number, number];
  return new Date(y, m - 1, d).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "2-digit" });
};

/**
 * The three default columns — this month, this quarter, year to date — built
 * from the two toggles. What every screen showed before the strip existed,
 * and what the dashboard's fixed summary still shows.
 */
export function periodColumns(comparison: Comparison, now = new Date()): PeriodColumn[] {
  return DEFAULT_PRESETS.map((preset) => resolveColumn(presetSelection(preset, now), comparison, now));
}

/** The little a drill-down needs to know about whose figures it is showing. */
export interface DrillTarget {
  id: number;
  name: string;
  currency: Currency | "";
  /** A currency total rather than a real client, which reads from a different endpoint. */
  synthetic?: boolean;
}

/**
 * One window's figures for one client, in that client's own currency.
 *
 * `ncm` is net contribution margin — sales less every cost the margin model
 * knows about — and is the number the sales team actually steers on, which is
 * why it sits in the cell rather than behind a drill-down.
 */
export interface Cell {
  gross: number;
  units: number;
  ncm: number;
  ncmPercent: number;
}

export const EMPTY_CELL: Cell = { gross: 0, units: 0, ncm: 0, ncmPercent: 0 };


/** Movement as a fraction; null when there is no base to compare against. */
export function ratio(current: number, previous: number): number | null {
  if (!previous) return null;
  return (current - previous) / previous;
}

const COMMAS = new Intl.NumberFormat(undefined, {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

export function withCommas(value: number): string {
  return COMMAS.format(value);
}

export function percent(value: number): string {
  return `${(value * 100).toFixed(2)}%`;
}

